import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Popup from "reactjs-popup";

import { useMainStore } from "@/contexts/Store";

import checkIcon from "../../../images/table-image/icon/check-icon.svg";
import pointsIconActive from "../../../images/table-image/icon/points-icon-active.svg";
import pointsIcon from "../../../images/table-image/icon/points-icon.svg";
import ConfirmationDialog from "../shared/ConfirmationDialog";
import Unlock from "../shared/Unlock";

type Props = {
  procedureRecordVersionId: number;
  isClosed?: boolean;
  isCompleted?: boolean;
};

function QAProcedureStepsContextMenu({
  procedureRecordVersionId,
  isClosed,
  isCompleted,
}: Props) {
  // Import MobX stores
  const mainStore = useMainStore();

  // State
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  // Variables
  const { canDeleteRecords } = mainStore.userPermissions;
  const buttonClasses = classNames("points-button", {
    "big-active": isOpenModal,
  });
  const listPointsClasses = classNames("list-points");
  const history = useHistory();
  // @ts-expect-error TS(2339) FIXME: Property 'record_version_id' does not exist on typ... Remove this comment to see the full error message
  const { record_version_id } = useParams();
  const testRecordVersionId = Number(record_version_id);
  const recordID = mainStore.recordVersions.list.find(
    (recordVersion) => recordVersion.id === procedureRecordVersionId,
  )?.record_id;
  const { hasModuleWriteAccess } = mainStore.userPermissions;
  const { workspaceID } = mainStore.context;

  // Functions
  function noButtonPressed() {
    handlePopUpClose();
    setIsDropdownOpen(false);
  }

  function deleteRowConfirmation() {
    setIsDropdownOpen(false);
    setIsDeleteConfirmationOpen(true);
  }

  async function deleteRow() {
    // Delete RecordVersion
    const { company } = mainStore.companies;
    if (company && company.id) {
      await mainStore.qa.deleteProcedureStep(recordID);
      mainStore.toast.setText("Procedure step deleted!");
    }
  }

  function handlePopUpOpen() {
    setIsOpenModal(true);
  }

  function handlePopUpClose() {
    setIsDropdownOpen(true);
    setIsOpenModal(false);
    setIsDeleteConfirmationOpen(false);
  }

  function close() {
    mainStore.qa.closeQAProcedure(procedureRecordVersionId);
  }

  function reOpen() {
    mainStore.qa.reopenQAProcedure(procedureRecordVersionId);
  }

  function handleClick() {
    history.push(
      `/workspaces/${workspaceID}/modules/qa-tests/${testRecordVersionId}/view-procedures/${procedureRecordVersionId}`,
    );
  }

  const renderTrigger = (
    <button
      className={buttonClasses}
      data-testid="qa-procedure-steps-context-menu-trigger"
    >
      <img className="points-icon" src={pointsIcon} alt="points-icon" />
      <img
        className="points-icon-active"
        src={pointsIconActive}
        alt="points-icon-active"
      />
    </button>
  );

  const renderBaseContent = (
    <div
      className="table-dropdown policy-dropdown"
      data-testid="qa-procedure-steps-context-menu-dropdown"
    >
      <ul>
        <li onClick={handleClick} data-testid="view-detail">
          View Procedure Step Detail
        </li>
        {!isCompleted && canDeleteRecords && (
          <>
            <hr />
            <li
              onClick={deleteRowConfirmation}
              data-testid="qa-procedure-steps-context-menu-dropdown-delete"
            >
              Delete
            </li>
          </>
        )}
      </ul>
    </div>
  );

  const renderDeleteConfirmation = (
    <ConfirmationDialog
      heading="Delete Procedure"
      content="Are you sure you want to delete this procedure step? Actions are not reversible."
      handleConfirm={deleteRow}
      handleReject={noButtonPressed}
    />
  );

  const renderButton = isClosed ? (
    <Unlock
      onYes={reOpen}
      tableName="Default"
      moduleIdentifier="qa_procedures"
      buttonText="Reopen"
      actualModelName="Procedure"
    />
  ) : (
    <button className="finalize-active" onClick={close}>
      <img src={checkIcon} alt="upload-icon" />
      Close
    </button>
  );

  return (
    <div
      className={listPointsClasses}
      data-testid="qa-procedure-steps-context-menu"
    >
      {!isCompleted && hasModuleWriteAccess && renderButton}
      <Popup
        position="bottom right"
        trigger={renderTrigger}
        open={isOpenModal}
        onOpen={handlePopUpOpen}
        onClose={handlePopUpClose}
        keepTooltipInside
      >
        {isDropdownOpen && renderBaseContent}
        {isDeleteConfirmationOpen && (
          <div className="table-dropdown success-dropdown">
            {renderDeleteConfirmation}
          </div>
        )}
      </Popup>
    </div>
  );
}

QAProcedureStepsContextMenu.defaultProps = {
  isClosed: false,
};

export default observer(QAProcedureStepsContextMenu);
